import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { platformApi } from '../../api/endpoints/platform';
import { useApplication } from '../../hooks/useApplications';
import { Gate } from '../../components/permissions/Gate';
import type { Permission, Scope } from '../../api/types/permissions';
import type { TenantLevelLocal } from '../../api/types/PlatformLocal';

// Shell del drill-down de un tenant en el módulo Platform. Hereda el guard de scope PLATFORM del
// layout padre (_layout.platform). Carga el tenant del PATH (no el del user) y muestra un header con
// su displayName + un sub-nav de recursos (Usuarios/Roles/<niveles de la app>/Branding) sobre un
// <Outlet/>. Cada pestaña operará sobre ESTE tenantId.
//
// Proyecto "aplicaciones/niveles genéricos": los tabs de RECURSO ya NO se cablean por `tenantType`
// (Retailers/Tiendas/Grupos hardcodeados). Salen 1:1 de `tenant.levels` — los niveles que la APP del
// tenant definió, con su `displayName` de negocio. Un tenant cuya app tiene 0 niveles → 0 tabs de recurso.
export const Route = createFileRoute('/_authenticated/_layout/platform/tenants/$tenantId')({
    component: TenantShell,
});

// Sub-nav del shell, extraído para testearlo sin el router file-based (recibe lo que el shell
// deriva de la ruta: tenantId del path, displayName del tenant cargado, pathname activo). El shell
// real lo alimenta con Route.useParams() + useQuery(getTenant) + useRouterState.
export function TenantSubNav({
    tenantId,
    displayName,
    pathname,
    levels,
}: {
    tenantId: string;
    displayName?: string;
    pathname: string;
    /** Niveles de RECURSO de la app del tenant (`tenant.levels`). Decide los tabs de recurso. */
    levels?: TenantLevelLocal[];
}) {
    const base = `/platform/tenants/${tenantId}`;
    const tabs = tabsForLevels(levels);
    return (
        <div className="space-y-4">
            <div>
                <p className="text-sm text-muted-foreground">Tenant</p>
                <h1 className="text-2xl font-semibold">{displayName ?? tenantId}</h1>
            </div>

            <nav aria-label="Recursos del tenant" className="flex flex-wrap gap-1 border-b">
                {tabs.map((tab) => {
                    const to = `${base}/${tab.to}`;
                    const isActive = pathname === to || pathname.startsWith(`${to}/`);
                    // La mayoría de las tabs (comunes + niveles) gatean a scope TENANT/tenantId (lo
                    // cubre el platform '*' por rank): los niveles con `tenant.level.manage`. La tab
                    // Campos usa un permiso `platform.*` a scope PLATFORM (global, sin scopeRef de tenant).
                    const isPlatformScoped = tab.scope === 'PLATFORM';
                    return (
                        <Gate
                            key={tab.to}
                            permission={tab.permission}
                            scope={isPlatformScoped ? 'PLATFORM' : 'TENANT'}
                            scopeRef={isPlatformScoped ? undefined : tenantId}
                        >
                            {isActive ? (
                                <span
                                    aria-current="page"
                                    className="border-b-2 border-brand-accent px-3 py-2 text-sm font-semibold"
                                >
                                    {tab.label}
                                </span>
                            ) : (
                                <Link
                                    to={to}
                                    className="border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    {tab.label}
                                </Link>
                            )}
                        </Gate>
                    );
                })}
            </nav>
        </div>
    );
}

type TabDef = { to: string; label: string; permission: Permission; scope?: Scope };

// Tabs COMUNES a todo tenant, sin importar su app: Usuarios, Roles, Branding, Campos. (Seguridad/MFA
// se movió al modal "Editar tenant" — DEC-RBAC-049.)
// Las de RECURSO (los niveles de la app) se intercalan entre Roles y Branding (ver `tabsForLevels`),
// respetando el orden histórico (recursos después de Roles, antes de Branding). EXCEPCIÓN: "Campos"
// tiene un permiso propio `platform.tenant.userfields.manage` que vive a scope PLATFORM (global) → se
// marca `scope:'PLATFORM'` para que el Gate no exija scopeRef de tenant. El backend re-enforza siempre.
const COMMON_HEAD_TABS: TabDef[] = [
    { to: 'users', label: 'Usuarios', permission: 'tenant.user.read' },
    { to: 'roles', label: 'Roles', permission: 'tenant.role.list' },
];

// DEC-RBAC-049: la pestaña Seguridad (MFA) se consolidó en el modal "Editar tenant" → ya no vive acá.
const COMMON_TAIL_TABS: TabDef[] = [
    { to: 'branding', label: 'Branding', permission: 'tenant.branding.manage' },
    {
        to: 'user-fields',
        label: 'Campos',
        permission: 'platform.tenant.userfields.manage',
        scope: 'PLATFORM',
    },
];

// Permiso de gestión de los recursos de un nivel GENÉRICO: `tenant.level.manage` (SYSTEM, scope TENANT).
// Para los niveles nuevos (LEVEL_1/2/3) NO hay un permiso por recurso — toda la gestión de entidades de
// nivel va por esta key. El platform admin (scope PLATFORM '*') la cubre por rank; el backend re-enforza.
// Si en algún caso el permiso no alcanzara, el server-side igual gatea la pantalla.
const LEVEL_MANAGE_PERMISSION: Permission = 'tenant.level.manage';

/**
 * Tabs de RECURSO derivados de los niveles de la app del tenant (`tenant.levels`). Un tab por nivel,
 * con `label` = `level.displayName` (el nombre de negocio — NUNCA el `level` crudo). Todos los niveles
 * van a la pantalla GENÉRICA `/platform/tenants/{id}/levels/{level}`, gate `tenant.level.manage` a scope
 * TENANT del path.
 *
 * PURGA FASE 4 (DEC-RBAC-091): las pantallas BESPOKE de niveles semánticos legacy (retailers/stores/
 * groups) fueron eliminadas junto con el mapa `LEGACY_LEVEL_ROUTE`. Un tenant legacy no migrado (con
 * niveles RETAILER/STORE/GROUP) cae a la rama genérica; la pantalla `/levels/{level}` valida `isLevelName`
 * (solo LEVEL_*) → esos niveles semánticos ya no tienen pantalla de recurso hasta migrarse a LEVEL_N.
 *
 * Los niveles llegan en el orden que define la app (jerárquico), que es el orden natural de navegación del
 * recurso, así que se respeta (no se reordena alfabéticamente). `levels` ausente o vacío → sin tabs de recurso.
 */
function tabsForLevels(levels: TenantLevelLocal[] | undefined): TabDef[] {
    const resourceTabs: TabDef[] = (levels ?? []).map((lvl) => ({
        to: `levels/${lvl.level}`,
        label: lvl.displayName,
        permission: LEVEL_MANAGE_PERMISSION,
    }));
    return [...COMMON_HEAD_TABS, ...resourceTabs, ...COMMON_TAIL_TABS];
}

// Exportado para testear la RESOLUCIÓN de niveles con prioridad (tenant.levels → app.levels) sin el
// router file-based: el test mockea Route.useParams/useRouterState, platformApi.getTenant y useApplication.
export function TenantShell() {
    const { tenantId } = Route.useParams();
    const pathname = useRouterState({ select: (s) => s.location.pathname });

    const { data: tenant } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
    });

    // Niveles de RECURSO con prioridad (mismo patrón que EditTenantModal / T8: derivar de la app):
    //  1) `tenant.levels` si el backend ya enriqueció el detalle → fuente directa.
    //  2) si llega vacío/ausente y el tenant tiene `applicationId` → se resuelven de la APP
    //     (`useApplication(applicationId).levels`). `useApplication` tiene `enabled: !!applicationId`,
    //     así que sin applicationId la query NO dispara (no cuelga) → sin tabs de recurso.
    // El `useApplication` solo se necesita cuando `tenant.levels` viene vacío; cuando ya viene poblado
    // se ignora `app.levels` (no se espera la app). El shape de `app.levels` (LevelDef: { level,
    // displayName }) es estructuralmente el de `TenantLevelLocal`, así que alimenta el sub-nav directo.
    const tenantLevels = tenant?.levels;
    const needsApp = !tenantLevels?.length && !!tenant?.applicationId;
    const { data: app } = useApplication(needsApp ? tenant.applicationId : undefined);

    // Mientras la app carga (needsApp y `app` aún undefined) no se renderizan tabs de nivel a medias:
    // `resolvedLevels` queda undefined → solo se ven los comunes hasta que `app.levels` esté disponible.
    const resolvedLevels = tenantLevels?.length ? tenantLevels : needsApp ? app?.levels : undefined;

    return (
        <>
            <TenantSubNav
                tenantId={tenantId}
                displayName={tenant?.displayName}
                pathname={pathname}
                levels={resolvedLevels}
            />
            <div className="mt-4">
                <Outlet />
            </div>
        </>
    );
}
